import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { batch } from 'react-redux';
import { utils, FixedNumber } from 'ethers';
import {
  useAppDispatch,
  useAppSelector,
  useExchangeInit,
  useFromChainBalance,
  useNotification,
  useToChainWalletAddress,
} from '../../../hooks';

import Label from '../../Label';
import Notice from '../../Notice/Notice';
import Dropdown from '../../Dropdown';
import InputGroup from '../../InputGroup';
import InputText from '../../InputText';
import {
  selectBridgeSlice,
  setTokenTransferAmount,
  ChainExtended,
  setTxIsOngoing,
  setTxHasError,
  setAmountRage,
  setSelectedToChain,
  setConvertNow,
} from '../../../redux/slices/bridgeSlice';
import {
  selectModalSlice,
  setIsWalletApproved,
  setModal,
} from '../../../redux/slices/modalSlice';
import {
  resetSessionExpiredAt,
  selectWalletSlice,
  setDisconnectWallet,
  setToChainWalletProvider,
} from '../../../redux/slices/walletSlice';
import TransactionProgress from './TransactionProgress';

import ModalExchange from '../../ModalExchange';
import ModalExchangeAgreement from '../../ModalExchangeAgreement';
import ModalAmount from '../../ModalAmount';
import ModalWalletNotification from '../../ModalWalletNotification';
import ModalOverMaxAmount from '../../ModalOverMaxAmount';
import ModalConnectWallet from '../../ModalConnectWallet';
import { floorToDecimals } from '../../../utils/calc';

type AmountPercentage = 'MAX' | '50%' | '25%' | '10%';

const AmountPercentageOptions: AmountPercentage[] = [
  `MAX`,
  `50%`,
  `25%`,
  `10%`,
];

const Converter: FC = () => {
  const dispatch = useAppDispatch();
  const {
    selectedToken,
    fromChainToken,
    selectedFromChain,
    selectedToChain,
    chains,
    tokenTransferAmount,
    txIsOngoing,
    convertMaxAmount,
    convertMinAmount,
    fromChainHtlc,
  } = useAppSelector(selectBridgeSlice);

  const { isWalletApproved } = useAppSelector(selectModalSlice);

  const { toChainWalletProvider } = useAppSelector(selectWalletSlice);

  const [uiSelectedFromChain, setUiSelectedFromChain] =
    useState<ChainExtended>();
  const [uiSelectedToChain, setUiSelectedToChain] = useState<ChainExtended>();
  const [connectWalletModalState, setConnectWalletModalState] = useState<
    false | 'from' | 'to'
  >(false);

  const toChainWalletAddress = useToChainWalletAddress();

  const fromChainBalance = useFromChainBalance();
  const [tokenTransferAmountPercentage, setTokenTransferAmountPercentage] =
    useState<AmountPercentage | null>(null);

  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [showExchangeAgreementModal, setShowExchangeAgreementModal] =
    useState(false);
  const [amountValidationMsg, setAmountValidationMsg] = useState<string | null>(
    null,
  );
  const [amountValidationCaseFlag, setAmountValidationCaseFlag] =
    useState<boolean>(false);
  const [convertButtonDisable, setConvertButtonDisable] = useState(true);
  const [isShowOverMaxAmount, setIsShowOverMaxAmount] = useState(false);
  const notificationAction = useNotification();
  const exchangeInit = useExchangeInit();
  const [isShowAmountDangerMsg, setIsShowAmountDangerMsg] = useState(false);
  const estimatedTransferFee = useMemo(() => {
    return selectedFromChain?.symbol === `KS_BESU` ? 1 : 0.005;
  }, [selectedFromChain?.symbol]);

  const handleTokenTransferAmountPercentageChange = useCallback(
    (percentage: AmountPercentage | null) => {
      setIsShowAmountDangerMsg(false);
      if (!fromChainBalance) {
        return;
      }

      let amount = ``;

      if (percentage === `MAX`) {
        amount = utils.formatUnits(
          fromChainBalance,
          fromChainToken?.decimals || undefined,
        );

        if (fromChainHtlc?.isNative) {
          amount = floorToDecimals(Number(amount) - estimatedTransferFee);
        } else {
          amount = floorToDecimals(amount);
        }
      } else if (percentage === `50%`) {
        amount = utils.formatUnits(
          fromChainBalance.div(2),
          fromChainToken?.decimals || undefined,
        );
        amount = floorToDecimals(amount);
      } else if (percentage === `25%`) {
        amount = utils.formatUnits(
          fromChainBalance.div(4),
          fromChainToken?.decimals || undefined,
        );
        amount = floorToDecimals(amount);
      } else if (percentage === `10%`) {
        amount = utils.formatUnits(
          fromChainBalance.div(10),
          fromChainToken?.decimals || undefined,
        );
        amount = floorToDecimals(amount);
      } else {
        amount = ``;
      }

      dispatch(setTokenTransferAmount(amount));
      setTokenTransferAmountPercentage(percentage);
    },
    [
      dispatch,
      fromChainBalance,
      fromChainToken?.decimals,
      fromChainHtlc?.isNative,
      estimatedTransferFee,
    ],
  );

  const handleChooseFromChain = React.useCallback(
    (evt) => {
      if (toChainWalletProvider) {
        dispatch(setDisconnectWallet(toChainWalletProvider.symbol));
        dispatch(setToChainWalletProvider(null));
        dispatch(setSelectedToChain(null));
      }
      dispatch(setTokenTransferAmount(null));
      setTokenTransferAmountPercentage(null);
      setIsShowAmountDangerMsg(false);
      dispatch(setAmountRage(`from`));
      const selectedChain = chains.find(
        (chain) => chain.symbol === evt.target.dataset.chainSymbol,
      );
      if (!selectedChain) return;

      const isSupported = selectedChain.chainTokenList.some(
        (t) => selectedToken?.symbol && t.symbol.includes(selectedToken.symbol),
      );
      if (!isSupported) return;

      setUiSelectedToChain(undefined);
      setUiSelectedFromChain(selectedChain);

      batch(() => {
        setConnectWalletModalState(`from`);
      });
    },
    [chains, dispatch, selectedToken?.symbol, toChainWalletProvider],
  );

  // From 체인 옵션
  const fromChainSelectOptions = React.useMemo(
    () => [
      selectedFromChain?.name || `Select a chain`,
      ...chains.map((chain) => {
        const isSupported = chain.chainTokenList.some(
          (t) =>
            selectedToken?.symbol && t.symbol.includes(selectedToken?.symbol),
        );
        const isSelected = chain.name === selectedFromChain?.symbol;
        return (
          <li
            className={isSupported ? `menu-item` : `menu-item is-disable`}
            key={chain.symbol}
          >
            <a
              style={
                isSelected
                  ? {
                      backgroundColor: `#edf6fe`,
                      color: `#0D7FE9`,
                    }
                  : {}
              }
              data-chain-symbol={chain.symbol}
            >
              {chain.name}
            </a>
          </li>
        );
      }),
    ],
    [selectedFromChain, chains, selectedToken?.symbol],
  );

  const handleChooseToChain = React.useCallback(
    (evt) => {
      if (!selectedFromChain) {
        alert(`From체인을 선택해주세요.`);
        return;
      }
      dispatch(setAmountRage(`to`));
      const selectedChain = chains.find(
        (chain) => chain.symbol === evt.target.dataset.chainSymbol,
      );
      if (!selectedChain) {
        return;
      }

      const isSupported = selectedChain.chainTokenList.find(
        (t) =>
          selectedToken?.symbol && t.symbol.includes(selectedToken?.symbol),
      );
      if (!isSupported) return;

      setUiSelectedToChain(selectedChain);
      setConnectWalletModalState(`to`);
    },
    [selectedFromChain, dispatch, chains, selectedToken?.symbol],
  );

  const amountOptions = React.useMemo(
    () => [
      tokenTransferAmountPercentage || `Direct input`,
      ...AmountPercentageOptions.map((percentage) => (
        <li className="menu-item" key={percentage}>
          <a
            onClick={() =>
              handleTokenTransferAmountPercentageChange(percentage)
            }
          >
            {percentage}
          </a>
        </li>
      )),
      `|`,
      <li className="menu-item" key="direct">
        <a onClick={() => handleTokenTransferAmountPercentageChange(null)}>
          Direct input
        </a>
      </li>,
    ],
    [tokenTransferAmountPercentage, handleTokenTransferAmountPercentageChange],
  );

  const handleSendTransactionModal = React.useCallback(async () => {
    dispatch(setConvertNow(true));

    const balance = fromChainBalance
      ? FixedNumber.from(
          utils.formatUnits(
            fromChainBalance,
            fromChainToken?.decimals || undefined,
          ),
        )
      : `-`;
    const balanceFixed = floorToDecimals(balance);
    if (Number(tokenTransferAmount) < Number(convertMinAmount)) {
      setAmountValidationCaseFlag(true);
      setAmountValidationMsg(
        `The minimum conversion amount is ${convertMinAmount
          ?.toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, `,`)}${
          fromChainToken?.symbol
        }. Enter the amount again.`,
      );
    } else if (Number(tokenTransferAmount) > Number(balanceFixed)) {
      setAmountValidationCaseFlag(true);
      setAmountValidationMsg(
        ` Insufficient balance. Check the balance in your From Wallet.`,
      );
    } else if (Number(tokenTransferAmount) > Number(convertMaxAmount)) {
      // 100000개 초과 시 알림 모달 노출
      setIsShowOverMaxAmount(true);
    } else if (
      [
        toChainWalletAddress,
        tokenTransferAmount,
        selectedFromChain,
        selectedToChain,
      ].every((_) => !!_)
    ) {
      setShowExchangeModal(true);
    }
  }, [
    fromChainBalance,
    fromChainToken?.decimals,
    fromChainToken?.symbol,
    tokenTransferAmount,
    convertMinAmount,
    convertMaxAmount,
    toChainWalletAddress,
    selectedFromChain,
    selectedToChain,
    dispatch,
  ]);

  const handleTypeAmount = React.useCallback(
    (evt) => {
      setIsShowAmountDangerMsg(false);
      let amountString = evt.target.value.toString();

      // NOTE: 0으로 시작 금지
      if (Number(convertMinAmount) > 1) {
        if (amountString.charAt(0) === `0` || amountString.charAt(0) === `.`) {
          amountString = amountString.substring(1);
        }
      }

      // NOTE: .으로 시작할 경우 앞에 0 추가해주기
      if (amountString.charAt(0) === `.` && Number(convertMinAmount) < 1) {
        amountString = `0${amountString}`;
      }

      // NOTE: 소수점 5자리 까지만 가능하도록
      if (
        amountString.lastIndexOf(`.`) >= 1 ||
        amountString.charAt(0) === `.`
      ) {
        if (
          amountString.length - (amountString.lastIndexOf(`.`) + 1) > 5 ||
          amountString.length - (amountString.lastIndexOf(`.`) + 1) < 0
        ) {
          amountString = amountString.substring(
            0,
            amountString.length -
              amountString.substring(amountString.lastIndexOf(`.`) + 6).length,
          );
        }
      }

      // NOTE: 입력 가능한 최대금액 = 내가 보유한 자산
      if (!fromChainBalance) return;
      const amount = utils.formatUnits(
        fromChainBalance,
        fromChainToken?.decimals || undefined,
      );
      const balance = fromChainHtlc?.isNative
        ? floorToDecimals(Number(amount) - estimatedTransferFee)
        : floorToDecimals(Number(amount));

      if (Number(amountString) >= Number(balance)) {
        amountString = balance;
        // NOTE: coin max 전송일 경우 transfer fee를 제외한 부분만 가능하다는 문구 보여주는 로직
        if (fromChainHtlc?.isNative) setIsShowAmountDangerMsg(true);
      }

      // NOTE: max개 초과 입력 시(maxAmount가 0이 아닐 경우에만)
      if (
        !!convertMaxAmount &&
        Number(amountString) > Number(convertMaxAmount)
      ) {
        amountString = convertMaxAmount;
        setIsShowOverMaxAmount(true);
      }

      // NOTE: Amount set하기
      batch(() => {
        dispatch(setTokenTransferAmount(amountString || ``));
      });
    },
    [
      convertMinAmount,
      convertMaxAmount,
      dispatch,
      fromChainBalance,
      fromChainToken?.decimals,
      fromChainHtlc,
      estimatedTransferFee,
    ],
  );

  const toChainSelectOptions = React.useMemo(() => {
    if (!selectedFromChain) {
      return [`Select a chain`];
    }

    return [
      selectedToChain?.name || `Select a chain`,
      ...selectedFromChain.transferableChains
        // .filter((chain) => !chain.disabledOnFrom)
        .map((chainSymbol) => {
          const chain = chains.find((c) => c.symbol === chainSymbol);
          if (!chain) return null;

          const isSupported = chain.chainTokenList.find(
            (t) =>
              selectedToken?.symbol && t.symbol.includes(selectedToken?.symbol),
          );

          return (
            <li
              className={
                isSupported && chain.symbol !== selectedFromChain?.symbol
                  ? `menu-item`
                  : `menu-item is-disable`
              }
              key={chain.symbol}
            >
              <a
                style={
                  chain.name === selectedToChain?.name
                    ? {
                        backgroundColor: `#edf6fe`,
                        color: `#0D7FE9`,
                      }
                    : {}
                }
                data-chain-symbol={chain.symbol}
              >
                {chain.name}
              </a>
            </li>
          );
        }),
    ];
  }, [selectedFromChain, selectedToChain, chains, selectedToken?.symbol]);

  const onRequestClose = useCallback(() => {
    setConnectWalletModalState(false);
  }, []);

  useEffect(() => {
    dispatch(setAmountRage(tokenTransferAmountPercentage));
  }, [dispatch, tokenTransferAmountPercentage]);

  // Convert now 버튼 조건 충족 못할시 비활성화
  useEffect(() => {
    if (
      selectedToken &&
      selectedFromChain &&
      selectedToChain &&
      toChainWalletAddress &&
      tokenTransferAmount
    ) {
      setConvertButtonDisable(false);
    } else {
      setConvertButtonDisable(true);
    }
  }, [
    selectedFromChain,
    selectedToChain,
    selectedToken,
    toChainWalletAddress,
    tokenTransferAmount,
  ]);

  // 사용자가 동작할 시 resetSessionExpiredAt의 시간을 리셋
  // dependency는 input의 값들이 변경될때마다 실행되어야 하기 때문에 추가 하였습니다.
  useEffect(() => {
    dispatch(resetSessionExpiredAt());
  }, [
    dispatch,
    selectedFromChain,
    selectedToChain,
    selectedToken,
    toChainWalletAddress,
    tokenTransferAmount,
  ]);

  const handleSendTransaction = React.useCallback(async () => {
    dispatch(setTxIsOngoing(true));
    try {
      await exchangeInit();
    } catch (e) {
      console.error(e);
      dispatch(setTxHasError(true));
    }
  }, [dispatch, exchangeInit]);

  useEffect(() => {
    if (isWalletApproved === true) {
      setConnectWalletModalState(false);
    }
  }, [isWalletApproved]);

  // Amount 숫자와 . 외에 입력방지
  const handleKeyDown = useCallback((evt) => {
    if ([`-`, `+`, `e`, `E`].includes(evt.key) || evt.keyCode === 229) {
      evt.preventDefault();
    }
  }, []);

  useEffect(() => {
    setTokenTransferAmountPercentage(null);
    setIsShowAmountDangerMsg(false);
  }, [selectedToken]);

  return (
    <>
      {txIsOngoing ? (
        <TransactionProgress />
      ) : (
        <main
          className="form-exchange column col-9 wow fadeInRight"
          data-wow-delay="0.5s"
        >
          <div className="form-container">
            <div className="columns col-gapless">
              <Label eng="Asset" ko="" />
              <div className="column col-10">
                <button
                  className="btn btn-block btn-lg"
                  onClick={() => dispatch(setModal(`ChooseCoin`))}
                >
                  {selectedToken?.symbol || `Select a token`}
                  {` `}
                  <i className="icon icon-caret" />
                </button>
              </div>
            </div>
            <div className="columns col-gapless">
              <Label eng="From" ko="" />
              <div className="column">
                <Dropdown
                  right
                  options={fromChainSelectOptions}
                  onClick={handleChooseFromChain}
                  disabled={false}
                />
              </div>
              <div className="column col-auto">
                <span className="i-exchange">⇢</span>
              </div>
              <Label eng="To" ko="" />
              <div className="column">
                <Dropdown
                  right
                  options={toChainSelectOptions}
                  onClick={handleChooseToChain}
                  disabled={!selectedFromChain?.name}
                />
              </div>
            </div>

            <div className="columns col-gapless">
              <Label eng="Destination" ko="" />
              <div className="column col-10">
                <InputGroup>
                  <InputText
                    placeholder="Connection to recipient wallet needed. Please select the relevant recipient chain."
                    value={toChainWalletAddress || ``}
                    readOnly
                    id={
                      toChainWalletProvider
                        ? `walletLogo-${toChainWalletProvider.symbol}`
                        : undefined
                    }
                  />
                </InputGroup>
              </div>
            </div>
            <div className="columns col-gapless">
              <Label eng="Amount" ko="" />
              <div className="column col-10">
                <InputGroup>
                  <InputText
                    type="number"
                    min={convertMinAmount ?? `0.01`}
                    step="0.00001"
                    placeholder="Amount"
                    value={tokenTransferAmount || ``}
                    onChange={handleTypeAmount}
                    onKeyDown={handleKeyDown}
                  />
                  <Dropdown
                    right
                    options={amountOptions}
                    defaultItemNull="Direct input"
                    disabled={false}
                  />
                </InputGroup>
                {isShowAmountDangerMsg && (
                  <div className="transfer-amount-danger-error">
                    Only the balance (-{estimatedTransferFee}) amount can be
                    transferred.
                  </div>
                )}
              </div>
            </div>
            <Notice />
            <button
              onClick={handleSendTransactionModal}
              className="btn-exchange btn btn-lg btn-primary btn-block"
              disabled={convertButtonDisable}
            >
              Convert now
            </button>
          </div>
        </main>
      )}
      {connectWalletModalState && (
        <ModalConnectWallet
          selectedChain={
            connectWalletModalState === `to`
              ? uiSelectedToChain
              : uiSelectedFromChain
          }
          mode={connectWalletModalState}
          onRequestClose={onRequestClose}
          // reset={resetData}
        />
      )}
      <ModalExchange
        isShow={showExchangeModal}
        onRequestClose={() => setShowExchangeModal(false)}
        onAgree={() => {
          if (Notification.permission === `granted`) {
            notificationAction(`Bridge has started Converting`);
            handleSendTransaction();
            setShowExchangeModal(false);
          } else {
            setShowExchangeModal(false);
            setShowExchangeAgreementModal(true);
          }

          dispatch(setConvertNow(false));
        }}
      />
      <ModalExchangeAgreement
        isShow={showExchangeAgreementModal}
        onRequestClose={() => setShowExchangeAgreementModal(false)}
      />
      <ModalAmount
        isShow={amountValidationCaseFlag}
        amountValidation={amountValidationMsg}
        onRequestClose={() => setAmountValidationCaseFlag(false)}
      />
      <ModalWalletNotification
        isShow={isWalletApproved}
        onRequestClose={() => dispatch(setIsWalletApproved(false))}
      />
      <ModalOverMaxAmount
        isShow={isShowOverMaxAmount}
        onRequestClose={() => setIsShowOverMaxAmount(false)}
      />
    </>
  );
};

export default Converter;
